import { WANX_VIDEO_MEDIA_TYPES as TYPES, normalizeWanxVideoMedia } from '../../utils/wanxVideoCapabilities.js';
import { getSeedance25ReferenceMediaType } from '../../utils/seedance25Capabilities.js';

const frameTypes = new Set([TYPES.FIRST_FRAME, TYPES.LAST_FRAME]);
const transferableTypes = new Set([...frameTypes, TYPES.REFERENCE_IMAGE, TYPES.REFERENCE_VIDEO]);
const owns = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
const asImages = (items) => (Array.isArray(items) ? items : []).filter(item => item && (typeof item === 'string' || typeof item === 'object')).map((item, index) => (
  typeof item === 'string' ? { id: `restored-reference-${index}`, url: item } : { ...item, id: item.id || `restored-reference-${index}` }
)).filter(item => item.url);

// A draft may contain several modes' inputs. Capabilities constrain submission, not storage.
export const getActiveWanxDraftMedia = (media, mode) => {
  const items = Array.isArray(media) ? media : [];
  if (mode === 'text2video') return [];
  if (mode === 'reference') return items.filter(item => !frameTypes.has(item.type));
  if (mode === 'img2video') return items.filter(item => item.type === TYPES.FIRST_FRAME);
  if (mode === 'start-end') return items.filter(item => frameTypes.has(item.type));
  return items;
};

export const switchVideoReferenceBank = (draft, from, to) => {
  if (from === to) return draft;
  let refs = draft.images.refs;
  let start = draft.images.start;
  let end = draft.images.end;
  let videos = draft.guaRefVideos;
  if (from === 'gua') {
    refs = [...draft.guaRefImages.map((url, index) => ({
      ...(draft.guaRefMeta.get(url) || {}), id: `gua-reference-${index}`, url,
      name: draft.guaRefNames.get(url) || '', mediaType: 'image',
    })), ...videos.map((url, index) => ({ id: `gua-video-${index}`, url, mediaType: 'video', name: draft.guaRefNames.get(url) || '' })),
    ...draft.images.refs.filter(item => getSeedance25ReferenceMediaType(item) === 'audio')];
  } else if (from === 'wanx') {
    refs = draft.wanxMedia.filter(item => [TYPES.REFERENCE_IMAGE, TYPES.REFERENCE_VIDEO, TYPES.REFERENCE_AUDIO].includes(item.type));
    start = draft.wanxMedia.find(item => item.type === TYPES.FIRST_FRAME)?.url || null;
    end = draft.wanxMedia.find(item => item.type === TYPES.LAST_FRAME)?.url || null;
    videos = draft.wanxMedia.filter(item => item.type === TYPES.REFERENCE_VIDEO).map(item => item.url);
  }
  // Vega accepts image references only. Leave Wan3-only video/audio/file media
  // in wanxMedia so switching models does not destroy the user's draft.
  if (to === 'wand') {
    return { ...draft, images: { start, end, refs: asImages(refs).filter(item => getSeedance25ReferenceMediaType(item) === 'image') } };
  }
  if (to === 'wanx') {
    const retained = draft.wanxMedia.filter(item => from === 'wand'
      ? !frameTypes.has(item.type) && item.type !== TYPES.REFERENCE_IMAGE
      : !transferableTypes.has(item.type) && item.type !== TYPES.REFERENCE_AUDIO);
    if (from === 'wand') videos = [];
    const media = (type, url, name, id) => ({
      ...draft.wanxMedia.find(item => item.type === type && item.url === url),
      type, url, name: name || '', id,
    });
    return { ...draft, wanxMedia: [
      ...retained,
      ...(start ? [media(TYPES.FIRST_FRAME, start, '', 'draft-first-frame')] : []),
      ...(end ? [media(TYPES.LAST_FRAME, end, '', 'draft-last-frame')] : []),
      ...refs.map((item, index) => media({ image: TYPES.REFERENCE_IMAGE, video: TYPES.REFERENCE_VIDEO, audio: TYPES.REFERENCE_AUDIO }[getSeedance25ReferenceMediaType(item)], item.url, item.name, `draft-reference-${index}`)),
      ...videos.filter(url => !refs.some(item => item.url === url && getSeedance25ReferenceMediaType(item) === 'video')).map((url, index) => media(TYPES.REFERENCE_VIDEO, url, draft.guaRefNames.get(url), `draft-video-${index}`)),
    ] };
  }
  if (to === 'gua') {
    const names = new Map(draft.guaRefNames);
    const metadata = new Map(draft.guaRefMeta);
    refs.forEach(item => { if (item.name) names.set(item.url, item.name); metadata.set(item.url, item); });
    return { ...draft, images: { ...draft.images, start, end, refs: asImages(refs) },
      guaRefImages: refs.filter(item => getSeedance25ReferenceMediaType(item) === 'image').map(item => item.url),
      guaRefVideos: [...new Set([...videos, ...refs.filter(item => getSeedance25ReferenceMediaType(item) === 'video').map(item => item.url)])],
      guaRefNames: names, guaRefMeta: metadata };
  }
  return { ...draft, guaRefVideos: videos, images: { start, end, refs: asImages(refs) } };
};

export const restoreVideoReferenceDraft = (draft, params, bank) => {
  // Missing metadata is unknown, never an instruction to delete the current draft.
  const next = { ...draft };
  if (bank === 'wanx') {
    if (Array.isArray(params.wanxMedia) && params.wanxMedia.length) {
      next.wanxMedia = normalizeWanxVideoMedia(params.wanxMedia);
    } else if (Array.isArray(params.referenceImages) && params.referenceImages.length) {
      next.wanxMedia = asImages(params.referenceImages).map((item, index) => ({ ...item,
        type: params.generationMode === 'img2video' && index === 0 ? TYPES.FIRST_FRAME
          : params.generationMode === 'start-end' && index < 2 ? (index === 0 ? TYPES.FIRST_FRAME : TYPES.LAST_FRAME)
            : TYPES.REFERENCE_IMAGE,
      }));
    }
  } else if (bank === 'gua') {
    if (Array.isArray(params.referenceImages) && params.referenceImages.length) next.guaRefImages = asImages(params.referenceImages).map(item => item.url);
    if (Array.isArray(params.guaRefVideos) && params.guaRefVideos.length) next.guaRefVideos = params.guaRefVideos;
    if (params.guaRefNamesObj) next.guaRefNames = new Map([...draft.guaRefNames, ...Object.entries(params.guaRefNamesObj)]);
    if (params.guaRefMetaObj) next.guaRefMeta = new Map([...draft.guaRefMeta, ...Object.entries(params.guaRefMetaObj)]);
  } else if (Array.isArray(params.referenceImages) && params.referenceImages.length) {
    next.images = { ...draft.images, refs: asImages(params.referenceImages) };
  }
  // Empty frame slots in old text/reference-mode snapshots are not deletions either.
  if (bank !== 'wanx' && ((owns(params, 'startImage') && params.startImage) || (owns(params, 'endImage') && params.endImage))) {
    next.images = { ...next.images,
      ...(params.startImage ? { start: params.startImage } : {}),
      ...(params.endImage ? { end: params.endImage } : {}),
    };
  }
  return next;
};

export const resolveVideoDraftMode = (mode, supportedModes) => {
  if (supportedModes.includes(mode)) return mode;
  if (mode === 'ref2v' && supportedModes.includes('reference')) return 'reference';
  if (mode === 'reference' && supportedModes.includes('ref2v')) return 'ref2v';
  return supportedModes[0] || 'text2video';
};
